<template>
  <div>
    <div v-if="!isUpsell && !inCart" :id="`category-${category.id}`" class="mt-8 mx-4 md:mx-2">
      <div class="flex items-center align-middle w-full flex-nowrap justify-between md:justify-start">
        <h2
          @click="toggleCategory"
          class="text-2xl font-bold text-gray-brand-900 tracking-tighter cursor-pointer align-middle"
        >
          {{ category.name }}
        </h2>
        <button
          v-if="offerCollapseFunctionality"
          @click="toggleCategory"
          class="ml-3 px-2 py-1 border border-gray-brand-900 text-xs text-gray-brand-900 rounded-full flex items-center transition-all flex-shrink-0"
        >
          <MinusIcon
            v-show="isCategoryVisible"
            class="w-3 h-3 mr-1"
            :class="{
              'opacity-0': !isCategoryVisible,
              'opacity-100': isCategoryVisible
            }"
          />
          <PlusIcon
            v-show="!isCategoryVisible"
            class="w-3 h-3 mr-1"
            :class="{
              'opacity-0': isCategoryVisible,
              'opacity-100': !isCategoryVisible
            }"
          />
          {{ isCategoryVisible ? 'Hide' : 'Show' }}
          <span v-if="!isCategoryVisible && itemsInCartCount > 0" class="ml-1 bg-gray-200 rounded-md px-1.5 py-0.5">
            <b>{{ itemsInCartCount }} in cart</b>
          </span>
        </button>
      </div>
      <h3 class="text-md text-gray-brand-700 w-full">
        {{ category.description }}
      </h3>
    </div>

    <div
      class="md:mx-0 flex flex-wrap justify-between transition-all duration-300"
      :class="{
        'mt-8 mb-8': true,
        'opacity-0 h-0 hidden': !isCategoryVisible,
        'opacity-100': isCategoryVisible
      }"
    >
      <template v-if="cartModals">
        <V2ProductCard
          v-for="card in productCards"
          :key="card.key"
          :product="card.product"
          :submittable-cart-item="card.submittableCartItem"
          :cart-quantity="card.cartQuantity"
          :cart-lines="card.cartLines"
          :v2-cart-item="card.serverCartItem"
          :pricing-tiers="pricingTiers"
          :is-upsell="isUpsell"
          :in-cart-view="inCart"
          :unavailable="unavailable"
        />
      </template>
      <template v-else>
        <V2ProductCardLegacy
          v-for="card in productCards"
          :key="card.key"
          :product="card.product"
          :submittable-cart-item="card.submittableCartItem"
          :v2-cart-item="card.serverCartItem"
          :pricing-tiers="pricingTiers"
          :is-upsell="isUpsell"
          @add-to-cart="bankItem"
        />
      </template>
    </div>
  </div>
</template>

<script>
import V2ProductCard from '@/components/v2/V2ProductCard'
import V2ProductCardLegacy from '@/components/v2/V2ProductCardLegacy.vue'
import { cartLineSignature } from '@/utils/submittableCartItem'
import { MinusIcon, PlusIcon } from '@heroicons/vue/solid'
import { posthog, getCommonTrackingProperties } from '@/composables/usePosthog'
import { ulid } from 'ulid'

export default {
  name: 'V2Category',

  components: {
    V2ProductCard,
    V2ProductCardLegacy,
    MinusIcon,
    PlusIcon
  },

  props: {
    category: {
      type: Object,
      required: true
    },
    pricingTiers: {
      type: Array,
      default: () => []
    },
    localCartItems: {
      type: Array,
      default: () => []
    },
    serverCartItems: {
      type: Array,
      default: () => []
    },
    offerCollapseFunctionality: {
      type: Boolean,
      default: false
    },
    isUpsell: {
      type: Boolean,
      default: false
    },
    inCart: {
      type: Boolean,
      default: false
    },
    // True only for the synthesized "Unavailable items" category.
    unavailable: {
      type: Boolean,
      default: false
    }
  },

  inject: {
    v2UpdateCartItem: { default: null }
  },

  data() {
    return {
      isCategoryVisible: true,
      bankedKeys: []
    }
  },

  computed: {
    cartModals() {
      return this.$store.getters.hasCartModals
    },
    drawerEnabled() {
      return this.$store.getters.hasCartDrawer
    },
    itemsInCartCount() {
      const cpspIds = this.category.products.map((p) => String(p.cpsp_id))
      return this.localCartItems
        .filter((item) => cpspIds.includes(String(item.category_product_store_pair_id)))
        .reduce((acc, item) => acc + (item.quantity || 0), 0)
    },
    productCards() {
      const cards = []
      this.category.products.forEach((product) => {
        const cpspId = String(product.cpsp_id)
        const localItems = this.localCartItems.filter((item) => String(item.category_product_store_pair_id) === cpspId)
        const cartQuantity = localItems.reduce((total, item) => total + (Number(item?.quantity) || 0), 0)

        // Only a card that *is* a cart line fans out one per line - the cart view, and the
        // unavailable list, whose products exist solely because the cart holds them and whose only
        // control is Remove. On the menu a card stands for the product, so it stays a single card
        // however many lines the cart holds; tapping it opens a blank modal, which is how a second
        // variation gets ordered. With the capability off every card is a line again, because the
        // legacy card customizes its own line in place.
        if (!this.cartModals) {
          if (this.drawerEnabled && !this.inCart) {
            // Cart drawer mode: the grid shows a single card per product — the
            // customization currently being configured. Lines "added to cart" via
            // the card button are banked (visible/editable in the drawer instead)
            // and the card resets to a fresh stub.
            const activeItems = localItems.filter((item) => !this.bankedKeys.includes(item.frontend_key))
            const activeItem = activeItems[activeItems.length - 1] || null
            if (activeItem) {
              const localIndex = localItems.indexOf(activeItem)
              cards.push({
                key: activeItem.frontend_key || `${product.id}-active`,
                product,
                cartQuantity,
                submittableCartItem: activeItem,
                serverCartItem:
                  this.serverCartItems.find(
                    (si, si_index) =>
                      String(si.category_product_store_pair_id) === cpspId &&
                      si_index === this.serverIndexFor(cpspId, localIndex)
                  ) || null
              })
            } else {
              const bankedForProduct = localItems.filter((item) => this.bankedKeys.includes(item.frontend_key)).length
              cards.push(this.stubCardFor(product, cartQuantity, bankedForProduct))
            }
            return
          }
          if (localItems.length === 0) {
            if (this.inCart) return
            cards.push(this.stubCardFor(product, cartQuantity))
            return
          }
        } else if (!this.inCart && !this.unavailable) {
          const upsellSuffix = this.isUpsell ? '-upsell' : ''
          cards.push({
            key: `stub-${product.cpsp_id}${upsellSuffix}`,
            product,
            cartQuantity,
            cartLines: localItems.filter((item) => (Number(item?.quantity) || 0) > 0),
            submittableCartItem: {
              quantity: 0,
              frontend_key: `stub-${product.cpsp_id}${upsellSuffix}`,
              category_product_store_pair_id: product.cpsp_id,
              cart_item_variant_options_attributes: []
            },
            serverCartItem: null
          })
          return
        }

        localItems.forEach((localItem, index) => {
          cards.push({
            key: localItem.frontend_key || `${product.id}-${index}`,
            product,
            cartQuantity,
            submittableCartItem: localItem,
            serverCartItem:
              this.serverCartItems.find(
                (si, si_index) =>
                  String(si.category_product_store_pair_id) === cpspId &&
                  si_index === this.serverIndexFor(cpspId, index)
              ) || null
          })
        })
      })
      return cards
    }
  },

  methods: {
    stubCardFor(product, cartQuantity = 0, generation = 0) {
      const upsellSuffix = this.isUpsell ? '-upsell' : ''
      const generationSuffix = generation > 0 ? `-b${generation}` : ''
      const key = `stub-${product.cpsp_id}${generationSuffix}${upsellSuffix}`
      return {
        key,
        product,
        cartQuantity,
        submittableCartItem: {
          quantity: 0,
          frontend_key: key,
          category_product_store_pair_id: product.cpsp_id,
          cart_item_variant_options_attributes: []
        },
        serverCartItem: null
      }
    },
    bankItem(frontendKey) {
      if (!frontendKey || this.bankedKeys.includes(frontendKey)) return
      // Same product + identical configuration as an existing line → merge
      // quantities into that line instead of stacking a duplicate.
      const item = this.localCartItems.find((i) => i.frontend_key === frontendKey)
      const twins =
        item && this.v2UpdateCartItem
          ? this.localCartItems.filter(
              (other) => other.frontend_key !== frontendKey && cartLineSignature(other) === cartLineSignature(item)
            )
          : []
      if (twins.length) {
        const [keep, ...rest] = twins
        const total = twins.reduce((acc, t) => acc + (t.quantity || 0), item.quantity || 0)
        this.v2UpdateCartItem({ ...keep, quantity: total })
        rest.forEach((t) => this.v2UpdateCartItem({ ...t, quantity: 0 }))
        this.v2UpdateCartItem({ ...item, quantity: 0 })
        return
      }
      this.bankedKeys.push(frontendKey)
    },
    serverIndexFor(cpspId, localIndex) {
      // Map local item index to server item index for matching server cart items
      let count = 0
      for (let i = 0; i < this.serverCartItems.length; i++) {
        if (String(this.serverCartItems[i].category_product_store_pair_id) === cpspId) {
          if (count === localIndex) return i
          count++
        }
      }
      return -1
    },
    toggleCategory() {
      if (this.offerCollapseFunctionality) {
        this.isCategoryVisible = !this.isCategoryVisible
        const formattedName = this.category.name?.toLowerCase().replace(/\s+/g, '_') || ''
        const eventName = this.isCategoryVisible
          ? `show_category_${formattedName}_${this.category.id}`
          : `hide_category_${formattedName}_${this.category.id}`
        posthog.capture(eventName, getCommonTrackingProperties(this))
      }
    }
  }
}
</script>
